import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface PerfilChange {
  id: number;
  cliente_id: number;
  campo_modificado: string;
  valor_anterior: string | null;
  valor_nuevo: string | null;
  modificado_por: string;
  created_at: string;
}

export function useAdminPerfilHistory(clienteId: number | undefined) {
  const [changes, setChanges] = useState<PerfilChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChanges = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: err } = await supabase
        .from('historial_perfil_changes')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('created_at', { ascending: false })
        .limit(100);
      if (err) {
        setError(err.message);
      } else {
        setChanges((rows as PerfilChange[]) ?? []);
      }
    } catch {
      setError('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  const recordChange = useCallback(async (
    campo: string,
    anterior: unknown,
    nuevo: unknown
  ) => {
    if (!clienteId) return;
    const valAnterior = anterior === null || anterior === undefined ? null : JSON.stringify(anterior);
    const valNuevo = nuevo === null || nuevo === undefined ? null : JSON.stringify(nuevo);
    if (valAnterior === valNuevo) return;
    try {
      await supabase
        .from('historial_perfil_changes')
        .insert({
          cliente_id: clienteId,
          campo_modificado: campo,
          valor_anterior: valAnterior,
          valor_nuevo: valNuevo,
        });
      await fetchChanges();
    } catch {
      // Silently fail history recording
    }
  }, [clienteId, fetchChanges]);

  return { changes, loading, error, recordChange, refresh: fetchChanges };
}